import React, { useState, useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { useData, uploadImage, uploadVideo, supabase } from '../context/DataContext';
import styles from './Admin.module.css';

// Default brand images for preview
import brand1 from '../assets/brand1.jpg';
import brand2 from '../assets/brand2.jpg';
import brand3 from '../assets/brand3.jpg';
import brand4 from '../assets/brand4.jpg';
import brand5 from '../assets/brand5.jpg';
const defaultBrands = [brand1, brand2, brand3, brand4, brand5];

// ── Input sanitizer ──
const clean = (val) => {
  if (typeof val !== 'string') return val;
  return DOMPurify.sanitize(val, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
};


const TABS = ['Genel Bakış', 'Artistler', 'Galeri', 'Etkinlikler', 'Site İçerikleri', 'Site Ayarları'];

export default function Admin() {
  const [tab, setTab] = useState(0);
  const [auth, setAuth] = useState(false);
  const [email, setEmail] = useState('');
  const [pwd, setPwd] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!supabase) { setLoading(false); return; }
    // Timeout: 3 sn içinde cevap gelmezse login formunu göster
    const timeout = setTimeout(() => setLoading(false), 3000);
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      clearTimeout(timeout);
      setAuth(!!session);
      setLoading(false);
    });
    return () => { clearTimeout(timeout); subscription.unsubscribe(); };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    const { error: err } = await supabase.auth.signInWithPassword({ email, password: pwd });
    if (err) {
      setError('Hatalı e-posta veya şifre');
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  if (loading) {
    return <div className={styles.loginPage}><div className={styles.loginBox}><p>Yükleniyor...</p></div></div>;
  }

  if (!auth) {
    return (
      <div className={styles.loginPage}>
        <div className={styles.loginBox}>
          <div className={styles.loginLogo}><span>X</span> HAUS</div>
          <p className={styles.loginSub}>Admin Paneli</p>
          <form onSubmit={handleLogin} className={styles.loginForm}>
            <input type="email" placeholder="E-posta" value={email} onChange={e => setEmail(e.target.value)} autoFocus />
            <input type="password" placeholder="Şifre" value={pwd} onChange={e => setPwd(e.target.value)} />
            {error && <p className={styles.loginError}>{error}</p>}
            <button type="submit" className="btn-primary" style={{ width: '100%' }}>Giriş</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.admin}>
      <aside className={styles.sidebar}>
        <div className={styles.sidebarLogo}><span>X</span> HAUS</div>
        <nav className={styles.sidebarNav}>
          {TABS.map((t, i) => (
            <button key={t} className={`${styles.sidebarItem} ${tab === i ? styles.active : ''}`} onClick={() => setTab(i)}>
              <span className={styles.sidebarIcon}>{['◈', '◉', '◫', '◷', '◩', '◎'][i]}</span>
              {t}
            </button>
          ))}
        </nav>
        <button onClick={handleLogout} className={styles.sidebarSite} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', font: 'inherit', textAlign: 'left', padding: 'inherit' }}>⏻ Çıkış Yap</button>
        <a href="/" className={styles.sidebarSite}>← Siteye Dön</a>
      </aside>
      <div className={styles.adminMain}>
        <div className={styles.adminHeader}>
          <h1 className={styles.adminTitle}>{TABS[tab]}</h1>
        </div>
        <div className={styles.adminContent}>
          {tab === 0 && <Overview />}
          {tab === 1 && <ArtistsPanel />}
          {tab === 2 && <GalleryPanel />}
          {tab === 3 && <EventsPanel />}
          {tab === 4 && <ContentPanel />}
          {tab === 5 && <SettingsPanel />}
        </div>
      </div>
    </div>
  );
}

/* ── OVERVIEW ── */
function Overview() {
  const { artists, gallery, settings, events } = useData();
  const activeEvents = events.filter(e => e.active);
  return (
    <div>
      <div className={styles.statsRow}>
        {[
          { n: artists.length, l: 'Artist' },
          { n: gallery.length, l: 'Galeri Fotoğrafı' },
          { n: activeEvents.length, l: 'Aktif Etkinlik' },
          { n: artists.filter(a => a.available).length, l: 'Müsait Artist' },
        ].map((s, i) => (
          <div key={i} className={styles.statCard}>
            <div className={styles.statBig}>{s.n}</div>
            <div className={styles.statLabel}>{s.l}</div>
          </div>
        ))}
      </div>
      <div className={styles.infoCard}>
        <h3 className={styles.infoTitle}>Stüdyo Bilgileri</h3>
        <p><strong>Ad:</strong> {settings.studioName}</p>
        <p><strong>Adres:</strong> {settings.address}</p>
        <p><strong>Instagram:</strong> {settings.instagram}</p>
        <p><strong>E-posta:</strong> {settings.email}</p>
        <p><strong>Çalışma Saatleri:</strong> {settings.hours}</p>
      </div>
    </div>
  );
}

/* ── IMAGE UPLOAD HELPER (with current preview) ── */
function ImageUploader({ value, onChange, label, currentImage, aspectHint }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Dosya tipi kontrolü
    if (!file.type.startsWith('image/')) return;
    // Max 10MB kontrol
    if (file.size > 10 * 1024 * 1024) {
      alert('Dosya boyutu 10MB\'dan büyük olamaz.');
      return;
    }
    setUploading(true);
    try {
      const url = await uploadImage(file);
      onChange(url);
    } catch (err) {
      console.error('Upload error:', err);
      const reader = new FileReader();
      reader.onload = ev => onChange(ev.target.result);
      reader.readAsDataURL(file);
    }
    setUploading(false);
  };

  return (
    <div className={`${styles.field} ${styles.full}`}>
      {label && <label>{label} {aspectHint && <span style={{ opacity: 0.4, fontSize: 9 }}>({aspectHint})</span>}</label>}

      {/* Mevcut görsel önizlemesi */}
      {currentImage && !value && (
        <div className={styles.currentPreview}>
          <span className={styles.currentLabel}>Şu anki görsel:</span>
          <img src={currentImage} alt="mevcut" className={styles.currentImg} />
        </div>
      )}

      <input type="file" accept="image/*" ref={fileRef} onChange={handleFile} style={{ display: 'none' }} />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button className="btn-outline" onClick={() => fileRef.current.click()} disabled={uploading}>
          {uploading ? 'Sıkıştırılıyor & Yükleniyor...' : 'Fotoğraf Yükle'}
        </button>
        {value && <button className={styles.deleteBtn} onClick={() => onChange(null)} style={{ fontSize: 10 }}>Kaldır</button>}
      </div>
      {value && (
        <div className={styles.newPreview}>
          <span className={styles.currentLabel}>Yeni görsel:</span>
          <img src={value} alt="preview" className={styles.photoPreview} />
        </div>
      )}
    </div>
  );
}

/* ── VIDEO UPLOADER ── */
function VideoUploader({ value, onChange, label }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('video/')) return;
    if (file.size > 100 * 1024 * 1024) { alert('Video 100MB\'dan büyük olamaz.'); return; }
    setUploading(true);
    try {
      const url = await uploadVideo(file);
      onChange(url);
    } catch (err) {
      console.error('Video upload error:', err);
    }
    setUploading(false);
  };

  return (
    <div className={`${styles.field} ${styles.full}`}>
      {label && <label>{label}</label>}
      <input type="file" accept="video/*" ref={fileRef} onChange={handleFile} style={{ display: 'none' }} />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button className="btn-outline" onClick={() => fileRef.current.click()} disabled={uploading}>
          {uploading ? 'Yükleniyor...' : 'Video Yükle'}
        </button>
        {value && <button className={styles.deleteBtn} onClick={() => onChange(null)} style={{ fontSize: 10 }}>Kaldır</button>}
      </div>
      {value && (
        <div className={styles.newPreview}>
          <span className={styles.currentLabel}>Mevcut video:</span>
          <video src={value} muted controls style={{ maxWidth: 200, marginTop: 6, borderRadius: 4 }} />
        </div>
      )}
    </div>
  );
}

/* ── GRID SLOT — tıklanabilir görsel slot (site layout mockup için) ── */
function GridSlot({ value, fallback, onChange, label, tall }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file || !file.type.startsWith('image/')) return;
    if (file.size > 10 * 1024 * 1024) { alert('Max 10MB'); return; }
    setUploading(true);
    try {
      const url = await uploadImage(file);
      onChange(url);
    } catch (err) {
      const reader = new FileReader();
      reader.onload = ev => onChange(ev.target.result);
      reader.readAsDataURL(file);
    }
    setUploading(false);
  };

  const src = value || fallback;
  return (
    <div
      className={`${styles.gridSlot} ${tall ? styles.gridSlotTall : ''}`}
      onClick={() => !uploading && fileRef.current.click()}
    >
      <input type="file" accept="image/*" ref={fileRef} onChange={handleFile} style={{ display: 'none' }} />
      {src && <img src={src} alt={label} />}
      <div className={styles.gridSlotOverlay}>
        <span className={styles.gridSlotLabel}>{label}</span>
        <span className={styles.gridSlotAction}>{uploading ? 'Yükleniyor...' : value ? 'Değiştir' : 'Yükle'}</span>
      </div>
      {value && (
        <button className={styles.gridSlotRemove} onClick={(e) => { e.stopPropagation(); onChange(null); }}>✕</button>
      )}
    </div>
  );
}

/* ── ARTISTS PANEL ── */
function ArtistsPanel() {
  const { artists, addArtist, updateArtist, deleteArtist } = useData();
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', style: '', bio: '', instagram: '', available: true, tags: '', photo: null });

  const openNew = () => { setForm({ name: '', style: '', bio: '', instagram: '', available: true, tags: '', photo: null }); setEditing('new'); };
  const openEdit = (a) => { setForm({ ...a, tags: a.tags.join(', ') }); setEditing(a.id); };
  const handleSave = () => {
    const data = {
      ...form,
      name: clean(form.name),
      style: clean(form.style),
      bio: clean(form.bio),
      instagram: clean(form.instagram),
      tags: form.tags.split(',').map(t => clean(t.trim())).filter(Boolean),
    };
    if (editing === 'new') addArtist(data);
    else updateArtist(editing, data);
    setEditing(null);
  };

  return (
    <div>
      <div className={styles.panelHeader}>
        <h2 className={styles.panelTitle}>Artistler ({artists.length})</h2>
        <button className="btn-primary" onClick={openNew}>+ Artist Ekle</button>
      </div>

      {editing && (
        <div className={styles.modal}>
          <div className={styles.modalBox}>
            <h3 className={styles.modalTitle}>{editing === 'new' ? 'Yeni Artist' : 'Düzenle'}</h3>
            <div className={styles.formGrid}>
              <div className={styles.field}>
                <label>İsim</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Artist adı" maxLength={100} />
              </div>
              <div className={styles.field}>
                <label>Stil</label>
                <input value={form.style} onChange={e => setForm(f => ({ ...f, style: e.target.value }))} placeholder="Fine Line · Blackwork" maxLength={200} />
              </div>
              <div className={`${styles.field} ${styles.full}`}>
                <label>Bio</label>
                <textarea rows={3} value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))} placeholder="Artist hakkında kısa açıklama" maxLength={1000} />
              </div>
              <div className={styles.field}>
                <label>Instagram</label>
                <input value={form.instagram} onChange={e => setForm(f => ({ ...f, instagram: e.target.value }))} placeholder="@artistadi" maxLength={50} />
              </div>
              <div className={`${styles.field} ${styles.full}`}>
                <label>Etiketler (virgülle ayırın)</label>
                <input value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} placeholder="Fine Line, Blackwork, Minimalist" maxLength={500} />
              </div>
              <ImageUploader label="Fotoğraf" value={form.photo} onChange={(url) => setForm(f => ({ ...f, photo: url }))} aspectHint="3:4 dikey" />
              <div className={styles.field}>
                <label>Randevu Durumu</label>
                <select value={form.available} onChange={e => setForm(f => ({ ...f, available: e.target.value === 'true' }))} className={styles.select}>
                  <option value="true">Açık</option>
                  <option value="false">Dolu</option>
                </select>
              </div>
            </div>
            <div className={styles.modalActions}>
              <button className="btn-primary" onClick={handleSave}>Kaydet</button>
              <button className="btn-outline" onClick={() => setEditing(null)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.artistsList}>
        {artists.map(a => (
          <div key={a.id} className={styles.artistRow}>
            <div className={styles.artistThumb}>
              {a.photo ? <img src={a.photo} alt={a.name} /> : <div className={styles.thumbPlaceholder}>{a.name[0]}</div>}
            </div>
            <div className={styles.artistRowInfo}>
              <div className={styles.artistRowName}>{a.name}</div>
              <div className={styles.artistRowStyle}>{a.style}</div>
              <div className={styles.artistRowTags}>{a.tags.join(' · ')}</div>
            </div>
            <span className={`${styles.badge} ${a.available ? styles.badgeOpen : styles.badgeClosed}`}>
              {a.available ? 'Açık' : 'Dolu'}
            </span>
            <div className={styles.rowActions}>
              <button className={styles.editBtn} onClick={() => openEdit(a)}>Düzenle</button>
              <button className={styles.deleteBtn} onClick={() => deleteArtist(a.id)}>Sil</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── GALLERY PANEL ── */
function GalleryPanel() {
  const { gallery, artists, addPhoto, updatePhoto, deletePhoto } = useData();
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', artist: '', artistId: null, style: '', featured: false, url: null });

  const openNew = () => { setForm({ title: '', artist: '', artistId: null, style: '', featured: false, url: null }); setEditing('new'); };
  const openEdit = (p) => { setForm({ ...p }); setEditing(p.id); };
  const handleSave = () => {
    const data = { ...form, title: clean(form.title), artist: clean(form.artist), style: clean(form.style) };
    if (editing === 'new') addPhoto(data);
    else updatePhoto(editing, data);
    setEditing(null);
  };
  const handleArtistSelect = (artistId) => {
    if (!artistId) { setForm(f => ({ ...f, artistId: null, artist: '' })); return; }
    const a = artists.find(x => x.id === Number(artistId));
    if (a) setForm(f => ({ ...f, artistId: a.id, artist: a.name }));
  };

  return (
    <div>
      <div className={styles.panelHeader}>
        <h2 className={styles.panelTitle}>Galeri ({gallery.length} fotoğraf)</h2>
        <button className="btn-primary" onClick={openNew}>+ Fotoğraf Ekle</button>
      </div>

      {editing && (
        <div className={styles.modal}>
          <div className={styles.modalBox}>
            <h3 className={styles.modalTitle}>{editing === 'new' ? 'Yeni Fotoğraf' : 'Düzenle'}</h3>
            <div className={styles.formGrid}>
              <div className={styles.field}>
                <label>Başlık</label>
                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Çalışma başlığı" maxLength={200} />
              </div>
              <div className={styles.field}>
                <label>Artist</label>
                <select value={form.artistId || ''} onChange={e => handleArtistSelect(e.target.value)} className={styles.select}>
                  <option value="">— Seçiniz —</option>
                  {artists.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
              <div className={styles.field}>
                <label>Stil</label>
                <input value={form.style} onChange={e => setForm(f => ({ ...f, style: e.target.value }))} placeholder="Fine Line" maxLength={100} />
              </div>
              <div className={styles.field}>
                <label>Öne Çıkar</label>
                <select value={form.featured} onChange={e => setForm(f => ({ ...f, featured: e.target.value === 'true' }))} className={styles.select}>
                  <option value="false">Hayır</option>
                  <option value="true">Evet</option>
                </select>
              </div>
              <ImageUploader label="Fotoğraf" value={form.url} onChange={(url) => setForm(f => ({ ...f, url }))} aspectHint="1:1 kare" />
            </div>
            <div className={styles.modalActions}>
              <button className="btn-primary" onClick={handleSave}>Kaydet</button>
              <button className="btn-outline" onClick={() => setEditing(null)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.galleryAdminGrid}>
        {gallery.map(item => (
          <div key={item.id} className={styles.galleryAdminCard}>
            <div className={styles.galleryAdminThumb}>
              {item.url ? <img src={item.url} alt={item.title} /> : <div className={styles.thumbPlaceholder}>{item.title}</div>}
            </div>
            <div className={styles.galleryAdminInfo}>
              <div className={styles.artistRowName}>{item.title}</div>
              <div className={styles.artistRowStyle}>{item.artist} · {item.style}</div>
              {item.featured && <span className={styles.featuredBadge}>Öne Çıkan</span>}
            </div>
            <div className={styles.rowActions}>
              <button className={styles.editBtn} onClick={() => openEdit(item)}>Düzenle</button>
              <button className={styles.deleteBtn} onClick={() => deletePhoto(item.id)}>Sil</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── EVENTS PANEL ── */
const EVENT_TYPES = ['WORKSHOP', 'ÖZEL GÜN', 'CONVENTION', 'KAMPANYA', 'AÇILIŞ', 'DİĞER'];

function EventsPanel() {
  const { events, addEvent, updateEvent, deleteEvent, setFeaturedEvent, pastEvents, addPastEvent, deletePastEvent } = useData();
  const [editing, setEditing] = useState(null);
  const emptyForm = { title: '', type: 'WORKSHOP', description: '', date: '', time: '', location: 'X HAUS Studio, Eskişehir', price: '', capacity: '', registered: 0, ctaLabel: 'Kayıt Ol', active: true, featured: false, image: null };
  const [form, setForm] = useState(emptyForm);
  const [pastForm, setPastForm] = useState({ title: '', date: '' });
  const [showPastForm, setShowPastForm] = useState(false);

  const openNew = () => { setForm(emptyForm); setEditing('new'); };
  const openEdit = (e) => { setForm({ ...e, capacity: e.capacity || '' }); setEditing(e.id); };
  const handleSave = () => {
    const data = {
      ...form,
      title: clean(form.title),
      description: clean(form.description),
      location: clean(form.location),
      price: clean(form.price),
      capacity: form.capacity ? Number(form.capacity) : null,
      registered: Number(form.registered) || 0,
    };
    if (editing === 'new') addEvent(data);
    else updateEvent(editing, data);
    setEditing(null);
  };
  const handleAddPast = () => {
    if (pastForm.title && pastForm.date) {
      addPastEvent({ title: clean(pastForm.title), date: clean(pastForm.date) });
      setPastForm({ title: '', date: '' });
      setShowPastForm(false);
    }
  };

  const f = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  return (
    <div>
      <div className={styles.panelHeader}>
        <h2 className={styles.panelTitle}>Etkinlikler ({events.length})</h2>
        <button className="btn-primary" onClick={openNew}>+ Etkinlik Ekle</button>
      </div>

      {editing && (
        <div className={styles.modal}>
          <div className={styles.modalBox} style={{ width: 700 }}>
            <h3 className={styles.modalTitle}>{editing === 'new' ? 'Yeni Etkinlik' : 'Etkinliği Düzenle'}</h3>
            <div className={styles.formGrid}>
              <div className={`${styles.field} ${styles.full}`}>
                <label>Etkinlik Adı</label>
                <input value={form.title} onChange={e => f('title', e.target.value)} placeholder="Tattoo Design Workshop #01" maxLength={200} />
              </div>
              <div className={styles.field}>
                <label>Tür</label>
                <select value={form.type} onChange={e => f('type', e.target.value)} className={styles.select}>
                  {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className={styles.field}>
                <label>Tarih</label>
                <input type="date" value={form.date} onChange={e => f('date', e.target.value)} className={styles.select} />
              </div>
              <div className={styles.field}>
                <label>Saat</label>
                <input value={form.time} onChange={e => f('time', e.target.value)} placeholder="10:00 — 17:00" maxLength={50} />
              </div>
              <div className={styles.field}>
                <label>Konum</label>
                <input value={form.location} onChange={e => f('location', e.target.value)} placeholder="X HAUS Studio, Eskişehir" maxLength={200} />
              </div>
              <div className={`${styles.field} ${styles.full}`}>
                <label>Açıklama</label>
                <textarea rows={3} value={form.description} onChange={e => f('description', e.target.value)} placeholder="Etkinlik hakkında kısa açıklama..." maxLength={2000} />
              </div>
              <div className={styles.field}>
                <label>Ücret</label>
                <input value={form.price} onChange={e => f('price', e.target.value)} placeholder="₺500 / Kişi · Malzeme Dahil" maxLength={100} />
              </div>
              <div className={styles.field}>
                <label>Buton Yazısı</label>
                <input value={form.ctaLabel} onChange={e => f('ctaLabel', e.target.value)} placeholder="Kayıt Ol" maxLength={50} />
              </div>
              <div className={styles.field}>
                <label>Toplam Kontenjan (boş = sınırsız)</label>
                <input type="number" value={form.capacity} onChange={e => f('capacity', e.target.value)} placeholder="12" min="0" />
              </div>
              <div className={styles.field}>
                <label>Kayıtlı Kişi Sayısı</label>
                <input type="number" value={form.registered} onChange={e => f('registered', e.target.value)} placeholder="0" min="0" />
              </div>
              <div className={styles.field}>
                <label>Durum</label>
                <select value={form.active} onChange={e => f('active', e.target.value === 'true')} className={styles.select}>
                  <option value="true">Aktif (Sitede Görünür)</option>
                  <option value="false">Gizli</option>
                </select>
              </div>
              <div className={styles.field}>
                <label>Öne Çıkan (Büyük Kart)</label>
                <select value={form.featured} onChange={e => f('featured', e.target.value === 'true')} className={styles.select}>
                  <option value="false">Hayır</option>
                  <option value="true">Evet — Bu etkinlik büyük gösterilir</option>
                </select>
              </div>
              <ImageUploader
                label="Etkinlik Görseli"
                value={form.image}
                onChange={(url) => f('image', url)}
                aspectHint="yatay, öne çıkan kartta sol tarafta görünür"
              />
            </div>
            <div className={styles.modalActions}>
              <button className="btn-primary" onClick={handleSave}>Kaydet</button>
              <button className="btn-outline" onClick={() => setEditing(null)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.eventsList}>
        {events.map(ev => (
          <div key={ev.id} className={`${styles.eventRow} ${!ev.active ? styles.eventInactive : ''}`}>
            <div className={styles.eventRowDate}>
              <div className={styles.eventDateDay}>{ev.date ? new Date(ev.date).getDate() : '—'}</div>
              <div className={styles.eventDateMonth}>{ev.date ? ['OCA','ŞUB','MAR','NİS','MAY','HAZ','TEM','AĞU','EYL','EKİ','KAS','ARA'][new Date(ev.date).getMonth()] : ''}</div>
            </div>
            <div className={styles.eventRowInfo}>
              <div className={styles.eventRowTitle}>
                {ev.title}
                {ev.featured && <span className={styles.featuredBadge}>★ Öne Çıkan</span>}
                {!ev.active && <span className={styles.hiddenBadge}>GİZLİ</span>}
              </div>
              <div className={styles.eventRowMeta}>
                <span className={styles.eventRowType}>{ev.type}</span><span>·</span>
                <span>{ev.time}</span><span>·</span><span>{ev.location}</span>
                {ev.capacity && <span>· {ev.registered}/{ev.capacity} kişi</span>}
              </div>
              <div className={styles.eventRowPrice}>{ev.price}</div>
            </div>
            <div className={styles.rowActions}>
              {!ev.featured && <button className={styles.editBtn} onClick={() => setFeaturedEvent(ev.id)} title="Öne Çıkar">★</button>}
              <button className={styles.editBtn} onClick={() => openEdit(ev)}>Düzenle</button>
              <button className={styles.deleteBtn} onClick={() => deleteEvent(ev.id)}>Sil</button>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.pastSection}>
        <div className={styles.panelHeader} style={{ marginTop: 40 }}>
          <h2 className={styles.panelTitle} style={{ fontSize: 20 }}>Geçmiş Etkinlikler ({pastEvents.length})</h2>
          <button className="btn-outline" onClick={() => setShowPastForm(true)}>+ Ekle</button>
        </div>
        {showPastForm && (
          <div className={styles.pastFormRow}>
            <div className={styles.field} style={{ flex: 1 }}>
              <input value={pastForm.title} onChange={e => setPastForm(p => ({ ...p, title: e.target.value }))} placeholder="Etkinlik adı" maxLength={200} />
            </div>
            <div className={styles.field} style={{ flex: '0 0 200px' }}>
              <input value={pastForm.date} onChange={e => setPastForm(p => ({ ...p, date: e.target.value }))} placeholder="Ay Yıl (ör: Mart 2024)" maxLength={50} />
            </div>
            <button className="btn-primary" onClick={handleAddPast}>Ekle</button>
            <button className="btn-outline" onClick={() => setShowPastForm(false)}>İptal</button>
          </div>
        )}
        <div className={styles.pastList}>
          {pastEvents.map(pe => (
            <div key={pe.id} className={styles.pastRow}>
              <span className={styles.pastRowTitle}>{pe.title}</span>
              <span className={styles.pastRowDate}>{pe.date}</span>
              <button className={styles.deleteBtn} onClick={() => deletePastEvent(pe.id)}>Sil</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── CONTENT PANEL ── */
function ContentPanel() {
  const { settings, updateSettings } = useData();
  const [form, setForm] = useState({ ...settings });
  const [saved, setSaved] = useState(false);
  const [newMarquee, setNewMarquee] = useState('');

  const save = () => {
    updateSettings(form);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const f = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const removeMarquee = (idx) => {
    const items = [...(form.marqueeItems || [])];
    items.splice(idx, 1);
    f('marqueeItems', items);
  };
  const addMarquee = () => {
    if (!newMarquee.trim()) return;
    f('marqueeItems', [...(form.marqueeItems || []), clean(newMarquee.trim().toUpperCase())]);
    setNewMarquee('');
  };

  const updateStat = (idx, key, val) => {
    const stats = [...(form.stats || [])];
    stats[idx] = { ...stats[idx], [key]: val };
    f('stats', stats);
  };
  const addStat = () => f('stats', [...(form.stats || []), { num: '', label: '' }]);
  const removeStat = (idx) => {
    const stats = [...(form.stats || [])];
    stats.splice(idx, 1);
    f('stats', stats);
  };

  const handleBrandImage = (idx, url) => {
    const images = [...(form.brandImages || [null, null, null, null, null])];
    images[idx] = url;
    f('brandImages', images);
  };

  return (
    <div>
      <div className={styles.panelHeader}>
        <h2 className={styles.panelTitle}>Site İçerikleri</h2>
        <button className="btn-primary" onClick={save}>{saved ? '✓ Kaydedildi!' : 'Tümünü Kaydet'}</button>
      </div>

      <div className={styles.settingsGrid}>
        {/* HERO */}
        <div className={styles.settingsSection}>
          <h3 className={styles.sectionTitle}>Hero Bölümü (Ana Sayfa Üst)</h3>
          <p className={styles.sectionHint}>Ana sayfanın en üstündeki büyük alan. Sağ tarafta görsel, solda yazılar yer alır.</p>
          <div className={styles.formGrid}>
            <div className={styles.field}>
              <label>Üst Etiket</label>
              <input value={form.heroTag || ''} onChange={e => f('heroTag', e.target.value)} placeholder="— Eskişehir / Est. 2024" maxLength={100} />
            </div>
            <div className={styles.field}>
              <label>Alt Başlık</label>
              <input value={form.heroSubtitle || ''} onChange={e => f('heroSubtitle', e.target.value)} placeholder="Tatto · Art · Studio" maxLength={100} />
            </div>
            <div className={styles.field}>
              <label>CTA Buton Yazısı</label>
              <input value={form.heroCtaText || ''} onChange={e => f('heroCtaText', e.target.value)} placeholder="Randevu Al" maxLength={50} />
            </div>
            <div className={styles.field}>
              <label>İkincil Buton Yazısı</label>
              <input value={form.heroCtaSecondary || ''} onChange={e => f('heroCtaSecondary', e.target.value)} placeholder="Galeriyi Gör" maxLength={50} />
            </div>
            <div className={`${styles.field} ${styles.full}`}>
              <label>Hero Medya Tipi</label>
              <div style={{ display: 'flex', gap: 16, marginTop: 6 }}>
                {['video', 'image'].map(t => (
                  <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                    <input type="radio" name="heroMediaType" value={t} checked={(form.heroMediaType || 'video') === t} onChange={() => f('heroMediaType', t)} />
                    {t === 'video' ? 'Video' : 'Fotoğraf'}
                  </label>
                ))}
              </div>
            </div>
            {(form.heroMediaType || 'video') === 'image'
              ? <ImageUploader label="Hero Fotoğraf" value={form.heroImage} onChange={(url) => f('heroImage', url)} currentImage={defaultBrands[0]} aspectHint="dikey, min 1200x800px" />
              : <VideoUploader label="Hero Video" value={form.heroVideo} onChange={(url) => f('heroVideo', url)} />
            }
          </div>
        </div>

        {/* MARQUEE */}
        <div className={styles.settingsSection}>
          <h3 className={styles.sectionTitle}>Kayan Yazı (Marquee)</h3>
          <p className={styles.sectionHint}>Hero altında sürekli kayan kırmızı şeritteki yazılar.</p>
          <div className={styles.marqueeList}>
            {(form.marqueeItems || []).map((item, i) => (
              <div key={i} className={styles.marqueeItem}>
                <span>{item}</span>
                <button className={styles.deleteBtn} onClick={() => removeMarquee(i)} style={{ padding: '4px 10px', fontSize: 10 }}>✕</button>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <input value={newMarquee} onChange={e => setNewMarquee(e.target.value)} placeholder="Yeni metin ekle..." style={{ flex: 1 }} maxLength={50} onKeyDown={e => e.key === 'Enter' && addMarquee()} />
            <button className="btn-outline" onClick={addMarquee}>Ekle</button>
          </div>
        </div>

        {/* BRAND GRID */}
        <div className={styles.settingsSection}>
          <h3 className={styles.sectionTitle}>Marka Görsel Grid</h3>
          <p className={styles.sectionHint}>Aşağıdaki mockup sitedeki grid layout'un birebir aynısıdır. Görselin üstüne tıklayarak değiştirebilirsiniz.</p>

          {/* Mini mockup — site layout'unun aynısı */}
          <div className={styles.brandMockup}>
            {/* Üst sıra: 2 görsel yan yana */}
            <div className={styles.brandMockupRow2}>
              <GridSlot
                idx={2} label="1"
                value={(form.brandImages || [])[2]}
                fallback={defaultBrands[2]}
                onChange={(url) => handleBrandImage(2, url)}
              />
              <GridSlot
                idx={3} label="2"
                value={(form.brandImages || [])[3]}
                fallback={defaultBrands[3]}
                onChange={(url) => handleBrandImage(3, url)}
              />
            </div>
            {/* Alt sıra: 2 görsel + 1 kırmızı kutu */}
            <div className={styles.brandMockupRow3}>
              <GridSlot
                idx={1} label="3"
                value={(form.brandImages || [])[1]}
                fallback={defaultBrands[1]}
                onChange={(url) => handleBrandImage(1, url)}
              />
              <GridSlot
                idx={4} label="4"
                value={(form.brandImages || [])[4]}
                fallback={defaultBrands[4]}
                onChange={(url) => handleBrandImage(4, url)}
              />
              <div className={styles.brandMockupRed}>
                <div className={styles.brandMockupRedTitle}>{(form.brandRedTitle || 'X\nHAUS').split('\n').join(' ')}</div>
                <div className={styles.brandMockupRedSub}>{form.brandRedSub || 'Art You Carry'}</div>
              </div>
            </div>
          </div>

          <p className={styles.sectionHint} style={{ marginTop: 16, marginBottom: 12 }}>Hero görseli (sağ taraf):</p>
          <div className={styles.brandMockupSingle}>
            <GridSlot
              idx={0} label="Hero"
              value={(form.brandImages || [])[0] || form.heroImage}
              fallback={defaultBrands[0]}
              onChange={(url) => { handleBrandImage(0, url); f('heroImage', url); }}
              tall
            />
          </div>

          <div className={styles.formGrid} style={{ marginTop: 24 }}>
            <div className={styles.field}>
              <label>Kırmızı Kutu Başlık</label>
              <input value={(form.brandRedTitle || '').replace('\n', ' ')} onChange={e => f('brandRedTitle', e.target.value.replace(' ', '\n'))} placeholder="X HAUS" maxLength={20} />
            </div>
            <div className={styles.field}>
              <label>Kırmızı Kutu Alt Yazı</label>
              <input value={form.brandRedSub || ''} onChange={e => f('brandRedSub', e.target.value)} placeholder="Art You Carry" maxLength={50} />
            </div>
          </div>
        </div>

        {/* ABOUT */}
        <div className={styles.settingsSection}>
          <h3 className={styles.sectionTitle}>Hakkımızda Bölümü</h3>
          <p className={styles.sectionHint}>Görsel grid'in altındaki "House of Line" başlıklı bölüm.</p>
          <div className={styles.formGrid}>
            <div className={`${styles.field} ${styles.full}`}>
              <label>Başlık</label>
              <input value={form.aboutHeading || ''} onChange={e => f('aboutHeading', e.target.value)} placeholder="HOUSE OF LINE." maxLength={100} />
            </div>
            <div className={`${styles.field} ${styles.full}`}>
              <label>Metin</label>
              <textarea rows={4} value={form.aboutText || ''} onChange={e => f('aboutText', e.target.value)} maxLength={2000} />
            </div>
            <div className={styles.field}>
              <label>Alıntı</label>
              <input value={form.aboutQuote || ''} onChange={e => f('aboutQuote', e.target.value)} placeholder="Art You Carry." maxLength={100} />
            </div>
          </div>
          <h4 className={styles.subTitle}>İstatistikler</h4>
          <p className={styles.sectionHint}>Hakkımızda bölümünün solundaki büyük rakamlar.</p>
          {(form.stats || []).map((s, i) => (
            <div key={i} className={styles.statsEditRow}>
              <input value={s.num} onChange={e => updateStat(i, 'num', e.target.value)} placeholder="5+" style={{ width: 80 }} maxLength={10} />
              <input value={s.label} onChange={e => updateStat(i, 'label', e.target.value)} placeholder="Uzman Artist" style={{ flex: 1 }} maxLength={50} />
              <button className={styles.deleteBtn} onClick={() => removeStat(i)} style={{ padding: '4px 10px', fontSize: 10 }}>✕</button>
            </div>
          ))}
          <button className="btn-outline" onClick={addStat} style={{ marginTop: 8, fontSize: 11 }}>+ İstatistik Ekle</button>
        </div>

        {/* SECTION LABELS */}
        <div className={styles.settingsSection}>
          <h3 className={styles.sectionTitle}>Bölüm Başlıkları</h3>
          <p className={styles.sectionHint}>Her bölümün üstündeki kırmızı etiket ve büyük başlık yazıları.</p>
          <div className={styles.formGrid}>
            <div className={styles.field}>
              <label>Artistler — Etiket</label>
              <input value={form.artistsSectionLabel || ''} onChange={e => f('artistsSectionLabel', e.target.value)} maxLength={50} />
            </div>
            <div className={styles.field}>
              <label>Artistler — Başlık</label>
              <input value={form.artistsSectionHeading || ''} onChange={e => f('artistsSectionHeading', e.target.value)} maxLength={50} />
            </div>
            <div className={styles.field}>
              <label>Galeri — Etiket</label>
              <input value={form.gallerySectionLabel || ''} onChange={e => f('gallerySectionLabel', e.target.value)} maxLength={50} />
            </div>
            <div className={styles.field}>
              <label>Galeri — Başlık</label>
              <input value={form.gallerySectionHeading || ''} onChange={e => f('gallerySectionHeading', e.target.value)} maxLength={50} />
            </div>
            <div className={styles.field}>
              <label>Etkinlikler — Etiket</label>
              <input value={form.eventsSectionLabel || ''} onChange={e => f('eventsSectionLabel', e.target.value)} maxLength={50} />
            </div>
            <div className={styles.field}>
              <label>Etkinlikler — Başlık</label>
              <input value={form.eventsSectionHeading || ''} onChange={e => f('eventsSectionHeading', e.target.value)} maxLength={50} />
            </div>
            <div className={`${styles.field} ${styles.full}`}>
              <label>Etkinlikler — Açıklama</label>
              <textarea rows={2} value={form.eventsDesc || ''} onChange={e => f('eventsDesc', e.target.value)} maxLength={500} />
            </div>
            <div className={styles.field}>
              <label>İletişim — Etiket</label>
              <input value={form.contactSectionLabel || ''} onChange={e => f('contactSectionLabel', e.target.value)} maxLength={50} />
            </div>
            <div className={styles.field}>
              <label>İletişim — Başlık</label>
              <input value={form.contactSectionHeading || ''} onChange={e => f('contactSectionHeading', e.target.value)} maxLength={50} />
            </div>
          </div>
        </div>

        {/* MAP */}
        <div className={styles.settingsSection}>
          <h3 className={styles.sectionTitle}>Harita</h3>
          <div className={styles.formGrid}>
            <div className={`${styles.field} ${styles.full}`}>
              <label>Google Maps Embed URL</label>
              <input value={form.mapEmbedUrl || ''} onChange={e => f('mapEmbedUrl', e.target.value)} placeholder="https://www.google.com/maps/embed?..." />
            </div>
            <div className={`${styles.field} ${styles.full}`}>
              <label>Google Maps Link</label>
              <input value={form.mapLink || ''} onChange={e => f('mapLink', e.target.value)} />
            </div>
            <div className={styles.field}>
              <label>Pin Etiket</label>
              <input value={form.mapPinLabel || ''} onChange={e => f('mapPinLabel', e.target.value)} maxLength={50} />
            </div>
            <div className={styles.field}>
              <label>Pin Adres</label>
              <input value={form.mapPinAddr || ''} onChange={e => f('mapPinAddr', e.target.value)} maxLength={100} />
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className={styles.settingsSection}>
          <h3 className={styles.sectionTitle}>Footer</h3>
          <div className={styles.formGrid}>
            <div className={styles.field}>
              <label>Telif Yazısı</label>
              <input value={form.footerCopy || ''} onChange={e => f('footerCopy', e.target.value)} maxLength={200} />
            </div>
            <div className={styles.field}>
              <label>Slogan</label>
              <input value={form.footerSlogan || ''} onChange={e => f('footerSlogan', e.target.value)} maxLength={100} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── SETTINGS PANEL ── */
function SettingsPanel() {
  const { settings, updateSettings } = useData();
  const [form, setForm] = useState({ ...settings });
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    updateSettings(form);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div>
      <div className={styles.panelHeader}>
        <h2 className={styles.panelTitle}>Site Ayarları</h2>
        <button className="btn-primary" onClick={handleSave}>{saved ? '✓ Kaydedildi!' : 'Kaydet'}</button>
      </div>

      <div className={styles.settingsGrid}>
        <div className={styles.settingsSection}>
          <h3 className={styles.sectionTitle}>Stüdyo Bilgileri</h3>
          <div className={styles.formGrid}>
            <div className={styles.field}>
              <label>Stüdyo Adı</label>
              <input value={form.studioName || ''} onChange={e => setForm(f => ({ ...f, studioName: e.target.value }))} maxLength={50} />
            </div>
            <div className={styles.field}>
              <label>Tagline</label>
              <input value={form.tagline || ''} onChange={e => setForm(f => ({ ...f, tagline: e.target.value }))} maxLength={100} />
            </div>
            <div className={`${styles.field} ${styles.full}`}>
              <label>Adres</label>
              <textarea rows={3} value={form.address || ''} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} maxLength={500} />
            </div>
            <div className={styles.field}>
              <label>E-posta</label>
              <input value={form.email || ''} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} maxLength={100} />
            </div>
            <div className={styles.field}>
              <label>Instagram</label>
              <input value={form.instagram || ''} onChange={e => setForm(f => ({ ...f, instagram: e.target.value }))} maxLength={50} />
            </div>
            <div className={styles.field}>
              <label>Telefon</label>
              <input value={form.phone || ''} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} maxLength={30} />
            </div>
            <div className={`${styles.field} ${styles.full}`}>
              <label>Çalışma Saatleri</label>
              <input value={form.hours || ''} onChange={e => setForm(f => ({ ...f, hours: e.target.value }))} maxLength={100} />
            </div>
          </div>
        </div>

        {/* EMAIL AYARLARI */}
        <div className={styles.settingsSection}>
          <h3 className={styles.sectionTitle}>E-posta Bildirimleri (EmailJS)</h3>
          <p className={styles.sectionHint}>
            Form gönderimlerinde e-posta almak için <a href="https://www.emailjs.com/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--red)' }}>emailjs.com</a>'dan
            ücretsiz hesap oluşturup aşağıdaki bilgileri doldurun. Randevu ve kayıt formlarından otomatik mail gelir.
          </p>
          <div className={styles.formGrid}>
            <div className={styles.field}>
              <label>EmailJS Service ID</label>
              <input value={form.emailServiceId || ''} onChange={e => setForm(f => ({ ...f, emailServiceId: e.target.value }))} placeholder="service_xxxxxxx" maxLength={50} />
            </div>
            <div className={styles.field}>
              <label>EmailJS Public Key</label>
              <input value={form.emailPublicKey || ''} onChange={e => setForm(f => ({ ...f, emailPublicKey: e.target.value }))} placeholder="xxxxxxxxxxxxxxxxx" maxLength={50} />
            </div>
            <div className={styles.field}>
              <label>İletişim Template ID</label>
              <input value={form.emailTemplateContact || ''} onChange={e => setForm(f => ({ ...f, emailTemplateContact: e.target.value }))} placeholder="template_contact" maxLength={50} />
            </div>
            <div className={styles.field}>
              <label>Randevu Template ID</label>
              <input value={form.emailTemplateBooking || ''} onChange={e => setForm(f => ({ ...f, emailTemplateBooking: e.target.value }))} placeholder="template_booking" maxLength={50} />
            </div>
            <div className={`${styles.field} ${styles.full}`}>
              <label>Bildirim E-posta Adresi</label>
              <input value={form.notificationEmail || ''} onChange={e => setForm(f => ({ ...f, notificationEmail: e.target.value }))} placeholder="info@xhaus.com" maxLength={100} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
